import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleQuestion } from "@fortawesome/pro-solid-svg-icons";
import { faCircleQuestion as faRegularCircleQuestion } from "@fortawesome/pro-regular-svg-icons";
import "../Input.scss";
import Tooltip from "../../tooltip/Tooltip";

export interface SliderInputProps {
    /* core behaviour */
    onChange?(value: number): void;
    forcevalue?: number; // external control

    /* range & stepping */
    min?: number;
    max?: number;
    step?: number;
    discrete?: boolean; // render clickable ticks

    /* cosmetic extras (kept from your original) */
    error?: boolean;
    success?: boolean;
    filedtitle?: React.ReactNode;
    help?: React.ReactNode;
    postchildren?: React.ReactNode;
    prechildren?: React.ReactNode;
}

const SliderInput: React.FC<SliderInputProps> = ({
    onChange,
    forcevalue,
    min = 0,
    max = 100,
    step = 1,
    discrete = false,
    ...props
}) => {
    /* ---------- State ---------- */
    const [value, setValue] = useState<number>(forcevalue ?? min);
    const [inputValue, setInputValue] = useState<string>(String(forcevalue ?? min));

    /* keep internal state in sync with external forcevalue */
    useEffect(() => {
        if (forcevalue !== undefined && forcevalue !== value) {
            setValue(forcevalue);
            setInputValue(String(forcevalue));
        }
    }, [forcevalue]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ---------- Helpers ---------- */
    const clamp = (v: number) => Math.min(max, Math.max(min, v));

    const update = (v: number) => {
        const nv = clamp(v);
        setValue(nv);
        setInputValue(String(nv));
        onChange?.(nv);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputVal = e.target.value;
        setInputValue(inputVal);

        const num = Number(inputVal);
        if (!Number.isNaN(num)) {
            // Don't clamp here - allow out of range values during typing
            setValue(num);
            onChange?.(num);
        }
    };

    const handleInputBlur = () => {
        const num = Number(inputValue);
        if (!Number.isNaN(num)) {
            // Clamp to range on blur
            const clampedValue = clamp(num);
            setValue(clampedValue);
            setInputValue(String(clampedValue));
            onChange?.(clampedValue);
        } else {
            // If invalid input, revert to current valid value
            setInputValue(String(value));
        }
    };

    const MAX_BARS = 25; // tweak or expose as prop

    /* --- ticks to render --- */
    const ticks = useMemo(() => {
        const raw: number[] = [];
        for (let i = min; i <= max; i += step) raw.push(i);

        // If too many, thin them out but keep first/last
        if (raw.length > MAX_BARS) {
            const stride = Math.ceil(raw.length / MAX_BARS);
            return raw.filter((_, i) => i % stride === 0 || i === raw.length - 1);
        }
        return raw;
    }, [min, max, step]);

    /* ---------- Render ---------- */
    const insertTitleOrHelp = props.filedtitle || props.help;
    const insertPreChildren = props.prechildren || insertTitleOrHelp;

    return (
        <div className="input-field-main-wrapper">
            {/* header */}
            {insertPreChildren && (
                <div
                    className={`input-field-header ${props.error ? "input-field-error" : ""}${
                        props.success ? " input-field-success" : ""
                    }`}
                >
                    {insertTitleOrHelp && (
                        <div className="input-field-title-container">
                            {props.filedtitle && <p>{props.filedtitle}</p>}
                            {props.help && (
                                <Tooltip title={props.help}>
                                    <FontAwesomeIcon icon={faRegularCircleQuestion} />
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {props.prechildren}
                </div>
            )}

            {/* slider + numeric input */}
            <div className="userful-slider-input-field-wrapper">
                <div className="userful-slider-input-field-inner-wrapper">
                    <input
                        className="userful-slider-input-field"
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={(e) => update(Number(e.target.value))}
                    />
                    {/* discrete ticks */}
                    {discrete && (
                        <div className="userful-slider-discrete">
                            {/* ❶ upper bar row */}
                            <div className="bar-row">
                                {ticks.map((t) => (
                                    <button
                                        key={t}
                                        className={`bar ${t <= value ? "filled" : ""}`}
                                        onClick={() => update(t)}
                                        aria-label={`Set value to ${t}`}
                                    />
                                ))}
                            </div>

                            {/* ❷ labels underneath */}
                            <div className="label-row">
                                {ticks.map((t) => (
                                    <span
                                        key={t}
                                        className={`label ${t === value ? "active" : ""}`}
                                        onClick={() => update(t)}
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <input
                    className="userful-input-field"
                    type="number"
                    value={inputValue}
                    step={step}
                    style={{ width: 74 }}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                />
            </div>

            {/* footer */}
            {props.postchildren && (
                <div
                    className={`input-field-container ${props.error ? "input-field-error" : ""}${
                        props.success ? " input-field-success" : ""
                    }`}
                >
                    {props.error && <FontAwesomeIcon icon={faCircleQuestion} />}
                    {props.success && <FontAwesomeIcon icon={faCheckCircle} />}
                    {props.postchildren}
                </div>
            )}
        </div>
    );
};

export default SliderInput;
